import React, { useEffect, useRef, useState } from 'react';
import { debounce } from 'lodash-es';
import { ChangeUsernameDialog, ChangeUsernameService } from '@colonist/clientforge';
import { MAX_USERNAME_LENGTH, MAX_GUEST_USERNAME_LENGTH } from '@colonist/consts';
import { LobbyImage } from '@colonist/types';
import { ProfileEditAjaxEndPoints } from '../../profile/ProfileEditAjaxEndPoints.ts';
import { UIAjaxWrapper } from '../../network/UIAjaxWrapper.ts';
import { UIAPIEndpoints } from '../../network/UIAPIEndpoints.ts';
import { UIUtilsEjsVariables } from '../../UIUtilsEjsVariables.ts';
import { navigateToPage } from '../../ui_utils/ClientUtils.ts';
import { runWithClickSound } from '../../ui_utils/AudioUtils.ts';
import { ViewPopupLogin } from '../ui_popup_membership_incentive/ViewPopupLogin.ts';
import { notificationController } from '../../EntryShared.ts';
const VALIDATION_DEBOUNCE_MS = 400;
export const ChangeUsernameDialogContainer = ({ onClose })=>{
    const [state, setState] = useState(null);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    const [service] = useState(()=>{
        const debouncedServerValidation = debounce((username, resolve)=>{
            void (async ()=>{
                const response = await UIAjaxWrapper.postRequestHandler(UIAPIEndpoints.apiValidateUsername(username), undefined, undefined);
                const serverValidation = response === null || response === void 0 ? void 0 : response.json;
                if (serverValidation == undefined) {
                    resolve({
                        type: 'isSame'
                    });
                    return;
                }
                if (serverValidation.containsToxicWord) {
                    resolve({
                        type: 'hasBadWords'
                    });
                    return;
                }
                if (!serverValidation.isAvailable) {
                    const suggestedUsernames = serverValidation.suggestedPostfixes.map((postfix)=>username + postfix);
                    resolve({
                        type: 'isTaken',
                        suggestedUsernames: suggestedUsernames
                    });
                    return;
                }
                resolve({
                    type: 'valid'
                });
            })();
        }, VALIDATION_DEBOUNCE_MS);
        return new ChangeUsernameService({
            onStateChange: setState,
            validateUsername: (username)=>{
                debouncedServerValidation.cancel();
                return new Promise((resolve)=>{
                    debouncedServerValidation(username, resolve);
                });
            },
            submitUsernameChange: async (username, shouldPurchase)=>{
                const success = await ProfileEditAjaxEndPoints.updateUsername(username, shouldPurchase);
                if (!success) return;
                notificationController.showNotification(LobbyImage.InfoIcon, {
                    key: 'strings:homePage.header.changeUsername.confirm.notification'
                });
                onCloseRef.current('ok');
            },
            onCancel: ()=>{
                onCloseRef.current('cancelled');
            }
        });
    });
    useEffect(()=>{
        let cancelled = false;
        const loadInfo = async ()=>{
            const data = await ProfileEditAjaxEndPoints.getUsernameChangeInfo();
            if (cancelled) return;
            if (data == undefined) return;
            service.setInitialData({
                currentUsername: data.currentUsername,
                isGuest: data.isGuest,
                freeChangesLeft: data.usernameChangeAttemptsLeft,
                maxLength: data.isGuest ? MAX_GUEST_USERNAME_LENGTH : MAX_USERNAME_LENGTH,
                colonistCoins: data.colonistCoins,
                changePrice: data.changePrice
            });
        };
        void loadInfo();
        return ()=>{
            cancelled = true;
        };
    }, [
        service
    ]);
    useEffect(()=>{
        return ()=>service.dispose();
    }, [
        service
    ]);
    if (state == null) return null;
    const handleConfirm = runWithClickSound(()=>service.submit());
    const handleCancel = runWithClickSound(()=>service.cancel());
    const handleGoToStore = runWithClickSound(()=>{
        onCloseRef.current('goToStore');
        navigateToStore();
    });
    const handleLogin = runWithClickSound(()=>{
        service.cancel();
        new ViewPopupLogin();
    });
    return /*#__PURE__*/ React.createElement(ChangeUsernameDialog, {
        onCancel: handleCancel,
        input: {
            value: state.username,
            maxLength: state.maxLength,
            autoFocus: !UIUtilsEjsVariables.isUserOnDiscordMobile,
            onChange: (value)=>service.setUsername(value)
        },
        validation: {
            result: state.validationResult,
            isValidating: state.isValidating,
            hasEdited: state.hasEdited
        },
        suggestions: {
            items: state.suggestedUsernames,
            onClick: (suggestion)=>service.setUsername(suggestion, true)
        },
        usernameChange: {
            freeChangesLeft: state.freeChangesLeft,
            colonistCoins: state.colonistCoins,
            price: state.changePrice,
            changeScenario: service.getChangeScenario()
        },
        title: getTitle(),
        onConfirm: handleConfirm,
        onGoToStore: handleGoToStore,
        onLogin: handleLogin
    });
};
const navigateToStore = ()=>{
    if (window.location.pathname == '/store') {
        const coinSection = document.getElementById('coins');
        coinSection === null || coinSection === void 0 ? void 0 : coinSection.scrollIntoView({
            behavior: 'smooth'
        });
        return;
    }
    navigateToPage('/store#coins', true, true);
};
const getTitle = ()=>{
    const context = UIUtilsEjsVariables.isUserOnDiscord ? 'discord' : '';
    return {
        key: 'strings:homePage.header.changeUsername.title',
        options: {
            context: context
        }
    };
};
